import type { AudioEngine, InstrumentId, SoundKey } from "./types";

const DEFAULT_VELOCITY = 0.9;

type InvokeFn = (cmd: string, args?: Record<string, unknown>) => Promise<unknown>;

/**
 * Tauri backend: samples live decoded in memory on the Rust side
 * (kira), and each note is a fire-and-forget IPC command. The import of
 * `@tauri-apps/api` is dynamic so it never enters the web bundle.
 */
export class NativeAudioEngine implements AudioEngine {
  readonly backend = "native" as const;

  private invoke: InvokeFn | null = null;
  private readonly loaded = new Set<InstrumentId>();

  async init(): Promise<void> {
    if (this.invoke) {
      return;
    }
    const core = await import("@tauri-apps/api/core");
    this.invoke = core.invoke;
    await this.invoke("audio_init");
  }

  unlock(): void {
    // Native audio is not subject to autoplay policies.
  }

  async loadInstrument(instrument: InstrumentId): Promise<void> {
    if (this.loaded.has(instrument)) {
      return;
    }
    if (!this.invoke) {
      await this.init();
    }
    await this.invoke?.("audio_load_instrument", { instrument });
    this.loaded.add(instrument);
  }

  isLoaded(instrument: InstrumentId): boolean {
    return this.loaded.has(instrument);
  }

  playNote(instrument: InstrumentId, key: SoundKey, velocity = DEFAULT_VELOCITY): void {
    // No await: Tauri's IPC queues the message and Rust fires the voice.
    void this.invoke?.("audio_play_note", { instrument, key, velocity });
  }

  allNotesOff(): void {
    void this.invoke?.("audio_all_notes_off");
  }

  dispose(): void {
    this.allNotesOff();
    this.loaded.clear();
  }
}
